import { useState, useRef, useEffect } from 'react';

// Tooltip character limit constant
const TOOLTIP_CHAR_LIMIT = 18;

interface UseConversationHoverReturn {
  hoveredConversationId: string | null;
  showTooltip: boolean;
  handleConversationMouseEnter: (conversationId: string, title: string) => void;
  handleConversationMouseLeave: () => void;
  clearHoverState: () => void;
  shouldShowTooltip: (title: string) => boolean;
  isHovered: (conversationId: string) => boolean;
}

/**
 * Custom hook to handle conversation hover logic and tooltip functionality
 * Separates tooltip state management from the main sidebar component
 */
export const useConversationHover = (): UseConversationHoverReturn => {
  // Tooltip state management
  const [hoveredConversationId, setHoveredConversationId] = useState<
    string | null
  >(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle conversation hover for tooltip
  const handleConversationMouseEnter = (
    conversationId: string,
    title: string,
  ) => {
    // Only show tooltip for titles longer than the character limit
    if (title.length <= TOOLTIP_CHAR_LIMIT) {
      return;
    }

    setHoveredConversationId(conversationId);

    // Clear any existing timeout to prevent multiple tooltips from appearing
    // This ensures only one tooltip can be active at a time
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Show tooltip after 3 seconds delay to avoid showing tooltips on quick mouse movements
    // This prevents tooltips from appearing when users are just scrolling through the sidebar
    hoverTimeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, 3000);
  };

  const handleConversationMouseLeave = () => {
    // Clear the timeout when mouse leaves to prevent tooltip from showing
    // This ensures tooltip only appears if user hovers for the full 3 seconds
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredConversationId(null);
    setShowTooltip(false);
  };

  const clearHoverState = () => {
    // Clear hover state and any pending tooltip timeout
    // This is useful when opening modals or other interactions that should clear hover state
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredConversationId(null);
    setShowTooltip(false);
  };

  // Helper functions for determining tooltip visibility
  const shouldShowTooltip = (title: string) =>
    title.length > TOOLTIP_CHAR_LIMIT;
  const isHovered = (conversationId: string) =>
    hoveredConversationId === conversationId;

  // Cleanup timeout on component unmount to prevent memory leaks
  // This ensures the timeout is cleared if the component is destroyed while timeout is pending
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return {
    hoveredConversationId,
    showTooltip,
    handleConversationMouseEnter,
    handleConversationMouseLeave,
    clearHoverState,
    shouldShowTooltip,
    isHovered,
  };
};
